import { IconCode } from "@multiweb/types/contracts";
import { IActionContext } from "UI/UI.Controls/Actions/IAction";
import { InvalidArgumentException } from "../../../../../Global/Base/Exceptions";
import { EnumUtils } from "../../../../../Global/Base/Utils/EnumUtils";
import { IControlWithXGAction, IInteractive } from "../../../../CommonUIInterfaces";
import { IEnabledVisualControl, IEnableInEditControl, IInteractiveControl } from "../../../../UI.BaseControls/ControlInterfaces";
import { Control } from "../../Controls/Control";
import { ControlParams, IInfoProvidingControl, IRunWithoutEdit } from "../../Controls/ControlInterfaces";
import { IControlInfoObject } from "../ControlInfo";
import { IFrame } from "../IFrame";
import { Button, ButtonType } from "./Button";
import { IPrimary } from "./IPrimary";
import { XGAction } from "./XGAction";

/**
 * Parametry konstruktoru tridy {@link XGButton}.
 */
export type XGButtonParams = ControlParams & {
    action: XGAction;
};

export class XGButton extends Control implements IInteractiveControl, IPrimary, IInteractive, IEnabledVisualControl, IControlWithXGAction, IInfoProvidingControl, IRunWithoutEdit {
    //#region Fields
    public readonly groupName: string;
    public readonly iconName: string;
    public readonly isEdit: boolean = false;
    public readonly priority: number;
    public readonly visibleMenu: boolean = false;
    public readonly isPrimary: boolean = false;

    /** Typ tlacika. Muze byt Raised nebo Flat - lisi se ve stylu */
    protected _type: ButtonType = ButtonType.raised;
    protected _button: Button;
    protected _borderColor: string;
    protected _color: string;
    protected _interactive: boolean = true;
    protected _enabledVisual: boolean = true;

    private _runWithoutEdit: boolean = false;
    private __isBusy: boolean = false;
    /** Příznak, jestli metoda uloží objekt před provedením */
    private _saveOnExecute: boolean;

    public get runWithoutEdit(): boolean {
        return this._runWithoutEdit;
    }

    private set _isBusy(value: boolean) {
        this.__isBusy = value;
        this._updateActiveState();
    }
    private get _isBusy(): boolean {
        return this.__isBusy;
    }

    public get saveOnExecute(): boolean {
        return this._saveOnExecute;
    }

    /** Tlačítko je přístupné pouze v edit. módu */
    public get enableInEdit(): boolean {
        return (this.xgAction instanceof IEnableInEditControl && this.xgAction.enableInEdit);
    }

    private _xgAction: XGAction;
    /**
     * Speciální ikona pro akci zadaná v definici formuláře (má přednost nad standardní ikonou)
     */
    private __icon?: IconCode;
    //#endregion Fields

    //#region Constructors
    constructor(params: XGButtonParams) {
        super(params);

        const { element, action } = params;

        if (action == null)
            throw new InvalidArgumentException("action");

        this._xgAction = action;

        action.disposableObjects?.add(this);

        this._events.add(action.visibilityChanged, () => {
            this._updateVisibleState();
            if (this.visibilityChanged)
                this.visibilityChanged.trigger(this, this.visible);
        });
        //this._events.add(action.enabledChanged, () => this._updateActiveState()); // TODO-rites

        this._events.addListener(element, "click", e => {
            if (!e.ctrlKey && !e.metaKey && this.isActive) // Při ctrl/cmd dojde ke zobrazení informací o komponentě
                this.execute();
        });
        this._button = new Button(this.getDataAttr("captionlockey"), { iconText: "", onLeft: true }, this._type, false, element as HTMLDivElement);

        this.groupName = element.getAttribute("groupname");
        this.iconName = element.getAttribute("iconName");
        this.isEdit = element.getAttribute("isedit") === "true";
        this.priority = +element.getAttribute("priority");
        this.element.classList.add("XGButton", "loader-enabled");
        this._runWithoutEdit = this.getBoolDataAttr("runwithoutedit", false);
        this.isPrimary = this.getBoolDataAttr("isPrimary");
        this._saveOnExecute = this.getBoolDataAttr("saveonexecute", false);

        this._setIcon();
        this._updateActiveState();
    }
    //#endregion Constructors

    //#region Properties
    public get xgAction(): XGAction {
        return this._xgAction;
    }

    public override set visible(value: boolean) {
        if (this.xgAction) {
            this.xgAction.visible = value;
        }

        this._updateVisibleState();
    }

    public override get visible(): boolean {
        return this.xgAction?.visible;
    }

    public override set enabled(value: boolean) {
        if (this.xgAction) {
            this.xgAction.enabled = value;
        }
    }

    public override get enabled(): boolean {
        return this.xgAction?.enabled;
    }

    public get enabledVisual(): boolean {
        return this._enabledVisual;
    }

    public set enabledVisual(value: boolean) {
        if (this.xgAction) {
            this.xgAction.enabledVisual = value;
        }
        this._enabledVisual = value;
        this._updateActiveState();
    }

    public get interactive(): boolean {
        return this._interactive;
    }

    public set interactive(value: boolean) {
        this._interactive = value;
    }

    public get isActive(): boolean {
        return this.enabled && this.interactive;
    }

    protected set _icon(icon: IconCode) {
        this.__icon = icon;
    }

    protected get _icon(): IconCode {
        return this.__icon;
    }

    public set owner(newOwner: IFrame) {
        if (this.xgAction) {
            this.xgAction.owner = newOwner;
        }
    }

    public get owner(): IFrame {
        return this.xgAction?.owner;
    }
    //#endregion Properties

    //#region Methods
    /**
     * Nastavi ikonu a jeji font podle nastaveni v definici
     */
    private _setIcon(): void {
        const iconName = this.getDataAttr("iconName")?.trim();
        if (iconName && EnumUtils.hasEnumValue(IconCode, iconName)) {
            this._icon = iconName as IconCode;
            this.element.classList.add("Icon", this._icon);
        } else {
            this._icon = null;
        }
    }

    protected _updateActiveState(): void {
        if (this.element == null)
            return;
        const active = this.enabledVisual && !this._isBusy;
        this.element.classList.toggle("Enabled", active);
        this.element.classList.toggle("Disabled", !active);
    }

    protected _updateVisibleState(): void {
        if (!this.element) {
            return;
        }
        if (this.element.parentElement && this.element.parentElement.classList.contains("ControlContainer")) {
            this.element.parentElement.style.display = this.visible ? "" : "none";
        }
        this.element.style.display = this.visible ? "" : "none";
    }

    public async execute(context?: IActionContext): Promise<void> {
        if (!this.isActive)
            return;

        this._isBusy = true;
        this.showLoader();
        try {
            if (this.owner)
                await this.owner.ownerForm.waitForBussyFrameAndChildren(this.owner);
            await this.xgAction.execute(context);
        } finally {
            this.hideLoader();
            this._isBusy = false;
        }
    }

    /** Vrací objekt, který obsahuje informace o prvku */
    public async getControlInfo(childInfo: Partial<IControlInfoObject>): Promise<Partial<IControlInfoObject>> {
        return {
            ...childInfo,
            locKey: this.locKey,
            additionalInfo: {
                dataAttributes: { ...this._getDataAttributes() },
                implementationClass: this.constructor.name,
            },
        };
    }

    public override dispose(): void {
        this._xgAction?.dispose();
        super.dispose();
    }

    //#endregion Methods
}
